// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a face for a ClickUp person. ClickUp profile pictures are usually
// empty and ClickUp often holds a different address than Konvo for the same
// person (jp@mountainhops.co.za in ClickUp, jp@nuvio.co.za in Konvo), so this
// finds the Konvo account by email, then by full name, then by a one-word
// ClickUp name that is the first name of exactly one Konvo person.

import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import type {MediaProxyImageSize} from '@fluxer/constants/src/MediaProxyImageSizes';
import {computed} from 'mobx';

export interface PhotoPerson {
	username?: string | null;
	name?: string | null;
	email?: string | null;
}

function norm(value: string | null | undefined): string {
	return (value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

interface PeopleIndex {
	byEmail: Map<string, User>;
	byName: Map<string, User | null>;
	byFirst: Map<string, User | null>;
}

/** Duplicate names map to null: a guess between two people is worse than initials. */
function add(map: Map<string, User | null>, key: string, user: User): void {
	if (!key) return;
	const existing = map.get(key);
	if (existing === undefined) map.set(key, user);
	else if (existing && existing.id !== user.id) map.set(key, null);
}

export function buildPeopleIndex(users: ReadonlyArray<User>): PeopleIndex {
	const index: PeopleIndex = {byEmail: new Map(), byName: new Map(), byFirst: new Map()};
	for (const user of users) {
		if (user.bot) continue;
		const email = norm(user.email);
		if (email) index.byEmail.set(email, user);
		const names = new Set([norm(user.globalName), norm(user.username)].filter(Boolean));
		for (const name of names) add(index.byName, name, user);
		const first = norm(user.globalName ?? user.username).split(' ')[0] ?? '';
		add(index.byFirst, first, user);
	}
	return index;
}

export function findPerson(index: PeopleIndex, person: PhotoPerson): User | null {
	const email = norm(person.email);
	if (email && index.byEmail.has(email)) return index.byEmail.get(email)!;
	const name = norm(person.username ?? person.name);
	if (!name) return null;
	const exact = index.byName.get(name);
	if (exact) return exact;
	if (exact === null) return null;
	return name.includes(' ') ? null : (index.byFirst.get(name) ?? null);
}

const peopleIndex = computed(() => buildPeopleIndex(Users.getUsers()));

/** The Konvo user behind a ClickUp or mail person, if there is exactly one. */
export function konvoUserFor(person: PhotoPerson): User | null {
	return findPerson(peopleIndex.get(), person);
}

/** Their Konvo profile photo; null when they have none (the caller shows initials). */
export function konvoPhotoFor(person: PhotoPerson, size: MediaProxyImageSize = 64): string | null {
	const user = konvoUserFor(person);
	if (!user?.avatar) return null;
	return getUserAvatarURL({id: user.id, avatar: user.avatar}, false, size);
}
